// src/components/WrapperUI/index.jsx
import { WrapperUIRoot, MenuContainer } from "./WrapperUI.styles";

import {
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from "react";
import { actionList, cameraOptions } from "../../app/actions-list";

import { ArcwareInit } from "@arcware-cloud/pixelstreaming-websdk/index.cjs.js";

import { InteractiveButtons } from "../InteractiveButtons";
import { DialogGeneric } from "../DialogGeneric";
import { StartScreen } from "../StartScreen";
import { LanguageSwitch } from "../LanguageSwitch";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";
import { PopUpTechDataAndContact } from "../PopUpTechDataAndContact";
import { WelcomeAndIntructionsPopUp } from "../WelcomeAndIntructionsPopUp";
import { FullScreenButton } from "../FullScreenButton";
import { useDevice } from "../../hooks/useDevice";
import { useResponsiveMenuLayout } from "../../hooks/useResponsiveMenuLayout";

const CONTAINER_ID = "shared-stream-container";

export const WrapperUI = () => {
  const {
    summaryOpen,
    closeSummary,
    toggleSummary,
    completeOptionActionIfMatch,
  } = useSelectedOptions();
  const rootRef = useRef(null);
  const wrapperRef = useRef(null);
  const { isIOS, isMobile } = useDevice(); // <-- use isMobile here
  const { containerRef, menuSize, animReady } = useResponsiveMenuLayout();

  const [videoInitialized, setVideoInitialized] = useState(false);
  const [applicationResponse, setApplicationResponse] = useState();
  const [arcwareApplication, setArcwareApplication] = useState();
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [shouldSendRes, setShouldSendRes] = useState(false);

  const handleSendResOnStart = (value) => {
    setShouldSendRes(value);
  };

  // measure the OUTER container (WrapperBody), never PlayerArea

  const sendResolutionCommand = useCallback(
    (width, height) => {
      if (!arcwareApplication?.emitUIInteraction) return;
      const descriptor = {
        Console: `r.setres ${Math.round(width)}x${Math.round(height)}w`,
      };
      arcwareApplication.emitUIInteraction(descriptor);
    },
    [arcwareApplication],
  );

  useLayoutEffect(() => {
    if (!wrapperRef.current || !arcwareApplication?.emitUIInteraction) return;

    let prevW = 0;
    let prevH = 0;
    let debounceTimer;

    const measureAndEmit = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { width, height } = rect;

      const w = Math.round(width);
      const h = Math.round(height);

      if (w !== prevW || h !== prevH) {
        prevW = w;
        prevH = h;
        sendResolutionCommand(w, h);
      }
    };

    // Emit once immediately when ready
    if (videoInitialized) measureAndEmit();

    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(measureAndEmit, 150);
    });

    observer.observe(wrapperRef.current);

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [
    arcwareApplication,
    videoInitialized,
    sendResolutionCommand,
    shouldSendRes,
  ]);

  // menu open/close
  const [isMenuOpen, setIsMenuOpen] = useState(true); // or false if you want closed by default
  const handleMenuToggle = (open) => {
    setIsMenuOpen(open);
  };

  // While anim is not ready, keep menu offscreen so it doesn’t flash.
  const effectiveMenuOpen = animReady ? isMenuOpen : false;

  const menuContainerStyle = isMobile
    ? {
        // mobile: full width at bottom, slide vertically
        left: 0,
        bottom: 0,
        width: "100%",
        height: `${menuSize}px`,
        transform: effectiveMenuOpen
          ? "translateY(0)"
          : `translateY(${menuSize}px)`,
      }
    : {
        // desktop: full height at left, slide horizontally
        top: 0,
        bottom: 0,
        margin: "auto",
        left: 0,
        height: "auto",
        width: `${menuSize}px`,
        transform: effectiveMenuOpen
          ? "translateX(0)"
          : `translateX(-${menuSize + 1}px)`,
      };

  const handleCloseStartScreen = (value) => {
    setShowStartScreen(value);
    const instructionsRead = localStorage.getItem("showInstructions");
    if (instructionsRead !== "false") setShowInstructions(!value);
  };

  const handleInstructionsShow = (value, reason) => {
    if (reason === "backdropClick") return;
    setShowInstructions(value);
    localStorage.setItem("showInstructions", "false");
  };

  // ==== Arcware init unchanged ====
  useEffect(() => {
    if (rootRef.current) return;

    const { PixelStreaming, Application } = ArcwareInit(
      {
        shareId: "share-4e2f583c-9431-4a80-b997-31e5c188e2df",
      },
      {
        initialSettings: {
          AutoConnect: true,
          TouchInput: true,
          customUI: true,
        },
        settings: {
          audioButton: false,
          fullscreenButton: false,
          micButton: false,
          settingsButton: false,
          infoButton: false,
          connectionStrengthIcon: false,
          stopButton: false,
        },
      },
    );

    if (window.location.hostname === "localhost") {
      window.arc = {
        app: Application,
        aps: PixelStreaming,
      };
    }

    setArcwareApplication(Application);
    Application.getApplicationResponse((response) => {
      setApplicationResponse(response);
    });

    PixelStreaming.videoInitializedHandler.add(() => setVideoInitialized(true));

    window.application = Application;
    rootRef.current = document.querySelector(`#${CONTAINER_ID}`);
    if (!rootRef.current) throw new Error("Ref.current is undefined.");
    rootRef.current.appendChild(Application.rootElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (videoInitialized && arcwareApplication) {
        arcwareApplication.stream.removePlayer();
      }
    };
  }, [arcwareApplication, videoInitialized]);

  useEffect(() => {
    completeOptionActionIfMatch(applicationResponse);
  }, [applicationResponse]);

  const showLanguageSwitch = !showInstructions && !summaryOpen;

  return (
    <>
      <WrapperUIRoot
        ref={containerRef}
        id={CONTAINER_ID}
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          opacity: showStartScreen ? 0 : 1,
        }}
      >
        {/* Start screen dialog */}
        <DialogGeneric
          show={showStartScreen}
          onHandleClose={handleCloseStartScreen}
          maxWidth="md"
          sx={{
            zIndex: 9999,
            ".MuiDialog-paper": { overflow: "hidden" },
          }}
        >
          <StartScreen
            onHandleClose={handleCloseStartScreen}
            videoInitialized={videoInitialized}
            arcwareApplication={arcwareApplication}
            onSendResOnStart={handleSendResOnStart}
          />
        </DialogGeneric>
        {videoInitialized && (
          <>
            {showLanguageSwitch && <LanguageSwitch />}
            {!isIOS && <FullScreenButton />}

            {/* MENU CONTAINER, size + slide fully controlled here */}
            <MenuContainer
              isMobile={isMobile}
              menuOpen={effectiveMenuOpen}
              animReady={animReady}
              sx={menuContainerStyle}
            >
              <InteractiveButtons
                emitUIInteraction={arcwareApplication?.emitUIInteraction}
                applicationResponse={applicationResponse}
                actionsList={actionList}
                loading={false}
                showingOverlay={showStartScreen}
                videoInitialized={videoInitialized}
                onMenuToggle={handleMenuToggle}
                menuOpen={effectiveMenuOpen}
                toggleSummary={toggleSummary}
                cameraOptions={cameraOptions}
                isMobile={isMobile}
              />
            </MenuContainer>
          </>
        )}
      </WrapperUIRoot>

      {/* Summary dialog */}
      <DialogGeneric
        show={summaryOpen}
        onHandleClose={(value, reason) => {
          if (reason === "backdropClick") return;
          if (!value) closeSummary();
        }}
        maxWidth="md"
        sx={{
          zIndex: 9999,
          ".MuiDialog-paper": { overflow: "hidden" },
        }}
      >
        <PopUpTechDataAndContact />
      </DialogGeneric>

      {/* Instructions dialog */}
      <DialogGeneric
        show={showInstructions}
        onHandleClose={handleInstructionsShow}
        maxWidth="md"
        sx={{ zIndex: 999 }}
      >
        <WelcomeAndIntructionsPopUp />
      </DialogGeneric>
    </>
  );
};
